import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Receipt, CheckCircle2, Clock, XCircle, HelpCircle, Loader2, ArrowLeft } from "lucide-react";

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const STATUS = {
  pending: { label: "Belum dikonfirmasi", cls: "bg-[var(--surface-hover)] text-[var(--text-secondary)]", icon: HelpCircle },
  waiting_verification: { label: "Menunggu verifikasi", cls: "bg-[var(--amber)]/15 text-amber", icon: Clock },
  approved: { label: "Disetujui · Aktif", cls: "bg-[var(--bull)]/15 text-bull", icon: CheckCircle2 },
  rejected: { label: "Ditolak", cls: "bg-[var(--bear)]/15 text-bear", icon: XCircle },
};
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "-"; } };

export default function Orders() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    api.get("/manual/my-orders")
      .then((r) => setOrders(r.data.orders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authLoading, user, navigate]);

  return (
    <div className="min-h-screen bg-[var(--surface-base)]">
      <div className="max-w-[900px] mx-auto px-4 lg:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/")} className="p-2 rounded-lg hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]" data-testid="orders-back"><ArrowLeft size={18} /></button>
          <Receipt size={22} className="text-accent" />
          <h1 className="font-heading font-extrabold text-2xl">Riwayat Pesanan Saya</h1>
        </div>

        {loading ? (
          <div className="card p-12 flex items-center justify-center"><Loader2 className="animate-spin text-accent" /></div>
        ) : orders.length === 0 ? (
          <div className="card p-12 text-center text-[var(--text-muted)]" data-testid="orders-empty">Belum ada pesanan.</div>
        ) : (
          <div className="space-y-2" data-testid="orders-list">
            {orders.map((o) => {
              const s = STATUS[o.status] || STATUS.pending;
              const Icon = s.icon;
              return (
                <div key={o.order_id} className="flex items-center gap-3 card px-4 py-3" data-testid={`order-${o.order_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-data text-xs text-accent">{o.order_id}</span>
                      <span className="text-[13px] font-semibold">{o.package_name}</span>
                      <span className="text-xs text-[var(--text-muted)]">· {o.days} hari</span>
                    </div>
                    <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{fmt(o.created_at)} · <span className="font-mono-data text-[var(--text-secondary)]">{rupiah(o.amount_total)}</span></p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0 ${s.cls}`} data-testid={`order-status-${o.order_id}`}>
                    <Icon size={12} /> {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
